import { createContext, useContext, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type { UserList } from '../types/user';

interface ListContextType {
  lists: UserList[];
  createList: (name: string) => Promise<UserList>;
  updateList: (listId: string, updates: Partial<UserList>) => Promise<void>;
  deleteList: (listId: string) => Promise<void>;
  addToList: (listId: string, productId: number) => Promise<void>;
  removeFromList: (listId: string, productId: number) => Promise<void>;
  getList: (listId: string) => UserList | undefined;
}

const ListContext = createContext<ListContextType | undefined>(undefined);

export const ListProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authState, updateUser } = useAuth();
  const user = authState.user;

  const lists = user?.lists || [];

  const updateUserLists = useCallback(async (updater: (lists: UserList[]) => UserList[]) => {
    if (!user) return;
    
    const updatedLists = updater([...lists]);
    await updateUser({ ...user, lists: updatedLists });
  }, [user, lists, updateUser]);

  const createList = useCallback(async (name: string): Promise<UserList> => {
    if (!user) throw new Error('User not authenticated');
    
    const newList: UserList = {
      id: `list-${Date.now()}`,
      name,
      productIds: [],
      isDefault: lists.length === 0, // First list is default
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await updateUserLists(lists => [...lists, newList]);
    return newList;
  }, [user, updateUserLists]);

  const updateList = useCallback(async (listId: string, updates: Partial<UserList>) => {
    await updateUserLists(lists => 
      lists.map(list => 
        list.id === listId 
          ? { ...list, ...updates, updatedAt: new Date() } 
          : list
      )
    );
  }, [updateUserLists]);

  const deleteList = useCallback(async (listId: string) => {
    await updateUserLists(lists => 
      lists.filter(list => list.id !== listId)
    );
  }, [updateUserLists]);

  const addToList = useCallback(async (listId: string, productId: number) => {
    await updateUserLists(lists => 
      lists.map(list => 
        list.id === listId && !list.productIds.includes(productId)
          ? { ...list, productIds: [...list.productIds, productId], updatedAt: new Date() }
          : list
      )
    );
  }, [updateUserLists]);

  const removeFromList = useCallback(async (listId: string, productId: number) => {
    await updateUserLists(lists => 
      lists.map(list => 
        list.id === listId
          ? { ...list, productIds: list.productIds.filter(id => id !== productId), updatedAt: new Date() }
          : list
      )
    );
  }, [updateUserLists]);

  const getList = useCallback((listId: string) => {
    return lists.find(list => list.id === listId);
  }, [lists]);

  return (
    <ListContext.Provider
      value={{
        lists,
        createList,
        updateList,
        deleteList,
        addToList,
        removeFromList,
        getList,
      }}
    >
      {children}
    </ListContext.Provider>
  );
};

export const useLists = (): ListContextType => {
  const context = useContext(ListContext);
  if (context === undefined) {
    throw new Error('useLists must be used within a ListProvider');
  }
  return context;
};
